// MyPurchases.jsx

import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, Link } from 'react-router-dom';
import { Package, ShoppingBag, Calendar, ExternalLink, CreditCard, Key, Download, Clock, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';

export default function MyPurchases() {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fixing, setFixing] = useState(false);
    const [confirmedAccess, setConfirmedAccess] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        fetchPurchases();
    }, []);

    const fetchPurchases = async () => {
        try {
            const response = await api.get('/api/stripe/my-purchases');
            setPurchases(response.data);

            // Initialize confirmed access state from backend data
            const confirmationState = {};
            response.data.forEach(purchase => {
                if (purchase.access_confirmed) {
                    confirmationState[purchase.id] = true;
                }
            });
            setConfirmedAccess(confirmationState);
        } catch (error) {
            console.error('Failed to fetch purchases:', error);
        } finally {
            setLoading(false);
        }
    };

    const fixPendingPurchases = async () => {
        setFixing(true);
        try {
            const response = await api.post('/api/stripe/fix-pending-purchases');
            alert(response.data.message);
            // Refresh purchases
            await fetchPurchases();
        } catch (error) {
            console.error('Failed to fix purchases:', error);
            alert('Failed to fix pending purchases');
        } finally {
            setFixing(false);
        }
    };

    const handleAccessConfirmation = async (purchaseId, isChecked) => {
        if (isChecked) {
            // Show confirmation dialog
            const confirmed = window.confirm(
                'Please confirm that you have received your access credentials from the software vendor.'
            );

            if (confirmed) {
                try {
                    // Save to backend
                    await api.post(`/api/stripe/purchases/${purchaseId}/confirm-access`);

                    // Update local state
                    setConfirmedAccess(prev => ({
                        ...prev,
                        [purchaseId]: true
                    }));

                    // Show success message
                    alert('Access confirmation recorded successfully!');
                } catch (error) {
                    console.error('Failed to confirm access:', error);
                    alert('Failed to save the confirmation. Please try again.');
                }
            }
        } else {
            // User unchecked - we don't allow unchecking once confirmed in the backend
            // So just update local state (won't persist)
            setConfirmedAccess(prev => ({
                ...prev,
                [purchaseId]: false
            }));
        }
    };

    const downloadInvoice = async (purchaseId) => {
        try {
            const response = await api.get(`/api/stripe/purchases/${purchaseId}/invoice`);

            // Open invoice in new tab
            window.open(response.data.invoice_url, '_blank');

        } catch (error) {
            console.error('Failed to get invoice:', error);
            alert('Failed to download the invoice. Please try again.');
        }
    };

    const copyLicenseKey = (licenseKey) => {
        navigator.clipboard.writeText(licenseKey);
        alert('License key copied to the clipboard!');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
    };

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    return (
        <MarketplaceLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Purchased Licenses</h1>
                            <p className="text-gray-600">Manage your software licenses and access activation instructions</p>
                        </div>
                        {/* Temporary fix button - remove after webhook is working */}
                        {process.env.NODE_ENV === 'development' && (
                            <button
                                onClick={fixPendingPurchases}
                                disabled={fixing}
                                className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${fixing ? 'animate-spin' : ''}`} />
                                {fixing ? 'Fixing...' : 'Fix Pending Purchases'}
                            </button>
                        )}
                    </div>
                </div>

                {purchases.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">No licenses purchased yet</h2>
                        <p className="text-gray-600 mb-6">Browse the marketplace to find software for your practice</p>
                        <button
                            onClick={() => navigate('/marketplace')}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            Browse Marketplace
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {purchases.map((purchase) => (
                            <div key={purchase.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-4">
                                            {purchase.app.icon_url ? (
                                                <img
                                                    src={purchase.app.icon_url}
                                                    alt={purchase.app.name}
                                                    className="w-16 h-16 rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                                    <Package className="w-8 h-8 text-white" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                    {purchase.app.name}
                                                </h3>

                                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4" />
                                                        <span>Purchased {formatDate(purchase.created_at)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <CreditCard className="w-4 h-4" />
                                                        <span>${purchase.amount} {purchase.currency}</span>
                                                    </div>
                                                    {purchase.expires_at && (
                                                        <div className={`flex items-center gap-1 ${isExpired(purchase.expires_at) ? 'text-red-600' : ''
                                                            }`}>
                                                            <Clock className="w-4 h-4" />
                                                            <span>
                                                                {isExpired(purchase.expires_at)
                                                                    ? 'Expired'
                                                                    : `Expires ${formatDate(purchase.expires_at)}`
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* License Key Section */}
                                                {purchase.license_status === 'assigned' && purchase.assigned_license_key ? (
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Key className="w-4 h-4 text-gray-600" />
                                                                <span className="text-sm font-medium text-gray-700">
                                                                    License Key:
                                                                </span>
                                                                <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                                                                    {purchase.assigned_license_key}
                                                                </code>
                                                            </div>
                                                            <button
                                                                onClick={() => copyLicenseKey(purchase.assigned_license_key)}
                                                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                                            >
                                                                Copy
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : purchase.license_status === 'manual_pending' ? (
                                                    <div className={`rounded-lg p-4 border ${confirmedAccess[purchase.id]
                                                        ? 'bg-green-50 border-green-200'
                                                        : 'bg-yellow-50 border-yellow-200'
                                                        }`}>
                                                        <div className="flex items-start gap-3">
                                                            {confirmedAccess[purchase.id] ? (
                                                                <>
                                                                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-green-800 mb-2">Access Confirmed</p>
                                                                        <p className="text-green-700 text-sm">
                                                                            You have confirmed receiving the access credentials.
                                                                        </p>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-yellow-800 mb-2">Access Pending</p>
                                                                        <p className="text-yellow-700 text-sm mb-3">
                                                                            You will receive your access credentials from the software vendor.
                                                                            For any additional information, please contact the vendor directly.
                                                                        </p>
                                                                        <label className="flex items-start gap-2 cursor-pointer group">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={confirmedAccess[purchase.id] || false}
                                                                                onChange={(e) => handleAccessConfirmation(purchase.id, e.target.checked)}
                                                                                className="mt-0.5 w-4 h-4 text-yellow-600 bg-white border-yellow-300 rounded focus:ring-yellow-500 focus:ring-2"
                                                                            />
                                                                            <span className="text-sm text-yellow-700 group-hover:text-yellow-800">
                                                                                I have received my access credentials.
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : purchase.license_status === 'out_of_stock' ? (
                                                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                                        <div className="flex items-start gap-2">
                                                            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                                                            <div className="text-sm">
                                                                <p className="font-medium text-red-800">License Unavailable</p>
                                                                <p className="text-red-700 mt-1">
                                                                    We're working on this. You'll receive your license via email soon.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Status Badge */}
                                        <div className="ml-4">
                                            {isExpired(purchase.expires_at) ? (
                                                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                                                    Expired
                                                </span>
                                            ) : (
                                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-4 pt-4 border-t flex items-center justify-between">
                                        <div className="flex gap-3">
                                            <Link
                                                to={`/marketplace/app/${purchase.app.slug}`}
                                                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                View App Details
                                            </Link>
                                            <button
                                                onClick={() => downloadInvoice(purchase.id)}
                                                className="flex items-center gap-2 text-gray-600 hover:text-gray-700 text-sm"
                                            >
                                                <Download className="w-4 h-4" />
                                                Download Invoice
                                            </button>
                                        </div>

                                        {isExpired(purchase.expires_at) && (
                                            <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
                                                Renew License
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </MarketplaceLayout>
    );
}